import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import Alert from "@mui/material/Alert";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import api, { installationMessage } from "services/device-installation-service";

const POLL_MS = 30000;
const MAX_POLLS = 40; // about 20 minutes of visible polling, then focus/refresh only

const SETUP_LABEL = {
  complete: "Setup: complete",
  needs_attention: "Setup: needs attention",
  pending: "Setup: in progress",
  none: "Setup: nothing selected",
};

const STATE_COLOR = { completed: "success", issued: "info", expired: "warning", revoked: "default" };

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

// Client-side fallback if an older backend omits lifecycle: status beats expiry.
function lifecycleOf(row) {
  if (row.lifecycle) return row.lifecycle;
  if (row.status === "completed") {
    return { state: "completed", label: "Completed - installation completed", completed_at: row.install_completed_at || row.consumed_at, installation_summary_available: true, can_install: false, can_revoke: false };
  }
  if (row.status === "revoked") {
    return { state: "revoked", label: row.consumed_reason === "device_installed" ? "Revoked - device installed using another claim" : "Revoked", can_install: false, can_revoke: false };
  }
  const expired = row.status === "issued" && row.expires_at && Date.now() >= new Date(row.expires_at).getTime();
  return { state: expired ? "expired" : row.status, label: expired ? "Expired" : row.status, can_install: row.status === "issued" && !expired && !row.consumed_at };
}

export default function DeviceClaimsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [copied, setCopied] = useState(null);
  const polls = useRef(0);
  const inFlight = useRef(false);

  const refresh = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    try {
      const data = await api.listClaims();
      setRows(data.filter((row) => row.claim_type !== "build" && row.build_id == null));
      setError("");
      setRefreshedAt(new Date());
    } catch (failure) {
      setError(installationMessage(failure));
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    // Installation may finish in another tab or on another device.
    const onFocus = () => refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    const onStorage = (event) => {
      if (event.key === "rivio_device_claims_changed") refresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("storage", onStorage);
    let channel = null;
    if (typeof BroadcastChannel !== "undefined") {
      channel = new BroadcastChannel("rivio-device-claims");
      channel.onmessage = () => refresh();
    }
    const timer = setInterval(() => {
      if (document.visibilityState !== "visible" || polls.current >= MAX_POLLS) return;
      polls.current += 1;
      refresh();
    }, POLL_MS);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("storage", onStorage);
      if (channel) channel.close();
      clearInterval(timer);
    };
  }, [refresh]);

  const copyLink = async (row) => {
    const link = `${window.location.origin}/onboard/claim/${encodeURIComponent(row.claim_code)}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(row.claim_id);
    } catch {
      setError("Could not copy the link. Open the claim and copy it from the address bar.");
    }
  };

  return (
    <DashboardLayout>
      <Helmet>
        <title>Device claims | Rivio</title>
      </Helmet>
      <DashboardNavbar />
      <MDBox pt={1}>
        <Card>
          <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <MDBox>
              <MDTypography variant="h5" component="h1" fontWeight="medium">
                Device claims
              </MDTypography>
              <MDTypography variant="body2" color="text" sx={{ opacity: 0.8, mt: 1 }}>
                Installation lifecycle from the server. Updates on focus, from other tabs, and every 30 seconds while visible.
                {refreshedAt ? ` Last refreshed ${refreshedAt.toLocaleTimeString()}.` : ""}
              </MDTypography>
            </MDBox>
            <MDButton variant="outlined" color="info" onClick={refresh} disabled={loading}>
              {loading ? <CircularProgress size={18} color="inherit" /> : "Refresh"}
            </MDButton>
          </MDBox>
          <Divider />
          <MDBox p={3} sx={{ overflowX: "auto" }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {rows == null ? (
              <CircularProgress size={28} />
            ) : rows.length === 0 ? (
              <MDTypography variant="body2">No device claims yet.</MDTypography>
            ) : (
              <Table size="small" aria-label="Device claims">
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    <TableCell>Claim</TableCell>
                    <TableCell>Device</TableCell>
                    <TableCell>Lifecycle</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const life = lifecycleOf(row);
                    const summary = row.installation_summary;
                    return (
                      <TableRow key={row.claim_id}>
                        <TableCell>#{row.claim_id}<br /><small>{row.one_time_use ? "one-time" : "reusable"}</small></TableCell>
                        <TableCell>{row.device_id ? `Device #${row.device_id}` : "-"}</TableCell>
                        <TableCell>
                          <Chip size="small" label={life.label} color={STATE_COLOR[life.state] || "default"} />
                          {life.state === "completed" && life.setup_state && (
                            <MDTypography variant="caption" display="block" mt={0.5} color={life.setup_state === "needs_attention" ? "error" : "text"}>
                              {SETUP_LABEL[life.setup_state] || life.setup_state}
                            </MDTypography>
                          )}
                          {life.state === "completed" && life.partial_history && (
                            <MDTypography variant="caption" display="block">Partial historical record</MDTypography>
                          )}
                          {life.reason && life.state !== "completed" && life.state !== "issued" && (
                            <MDTypography variant="caption" display="block">Reason: {String(life.reason).replace(/_/g, " ")}</MDTypography>
                          )}
                        </TableCell>
                        <TableCell>
                          {life.state === "completed" ? (
                            <MDTypography variant="caption" display="block">
                              Completed {formatDate(life.completed_at)}
                              {summary?.customer_name ? ` · ${summary.customer_name}` : ""}
                              {summary?.asset_name ? ` · ${summary.asset_name}` : ""}
                              {summary?.asset_identifier ? ` (${summary.asset_identifier})` : ""}
                              {summary?.installer_name ? ` · Installed by ${summary.installer_name}` : ""}
                            </MDTypography>
                          ) : life.state === "issued" || life.state === "expired" ? (
                            <MDTypography variant="caption" display="block">
                              {life.state === "expired" ? "Expired" : "Expires"} {formatDate(row.expires_at)} · Attempts {row.attempt_count}/{row.max_attempts}
                            </MDTypography>
                          ) : (
                            <MDTypography variant="caption" display="block">Read-only</MDTypography>
                          )}
                        </TableCell>
                        <TableCell>
                          <MDBox display="flex" gap={1} flexWrap="wrap">
                            {life.installation_summary_available && (
                              <MDButton size="small" variant="outlined" color="info" onClick={() => navigate(`/onboarding/device-claims/${row.claim_id}/summary`)}>
                                View installation
                              </MDButton>
                            )}
                            {life.installation_summary_available && !life.partial_history && (
                              <MDButton size="small" variant="text" color="info" onClick={() => navigate(`/onboarding/device-claims/${row.claim_id}/summary?print=1`)}>
                                Download summary
                              </MDButton>
                            )}
                            {life.can_install && (
                              <>
                                <MDButton size="small" variant="outlined" color="info" onClick={() => window.open(`/onboard/claim/${encodeURIComponent(row.claim_code)}`, "_blank", "noopener")}>
                                  Open
                                </MDButton>
                                <MDButton size="small" variant="text" color="info" onClick={() => copyLink(row)}>
                                  {copied === row.claim_id ? "Copied" : "Copy link"}
                                </MDButton>
                              </>
                            )}
                          </MDBox>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
